import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { Search, Building2, X, Check } from "lucide-react";

/**
 * SOUND-294 — Picker modal for adding a partner to an existing company's
 * sources[]. Searches partners via GET /companies?companyType=partner&search=X,
 * excludes any partner already attributed, and submits
 * POST /companies/:id/sources with { partnerId, role }.
 */
const ROLE_OPTIONS = [
  { value: "primary", label: "Primary", description: "The lead partner on this account." },
  { value: "secondary", label: "Secondary", description: "Supporting role alongside a primary partner." },
  { value: "equal", label: "Equal", description: "Shared credit — split between partners." },
];

export default function AddPartnerSourceModal({ company, onClose, onAdded }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState("primary");
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  // Collect partnerIds already in sources[] so we can exclude them from results
  const existingPartnerIds = new Set(
    Array.isArray(company?.sources)
      ? company.sources.map((s) => s?.partnerId).filter(Boolean)
      : []
  );

  // Debounced search
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPartners(query);
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fetchPartners = async (search) => {
    if (!effectiveCompanyId || !auth?.user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("companyType", "partner");
      params.set("limit", "25");
      if (search) params.set("search", search);

      const res = await makeApiRequest(
        "get",
        `${apiRoutes.getCompanies}?${params.toString()}`,
        auth.user,
        effectiveCompanyId
      );
      const all = Array.isArray(res?.data?.data) ? res.data.data : [];
      const filtered = all.filter((c) => {
        if (!c) return false;
        if (existingPartnerIds.has(c.companyId)) return false;
        if (c.companyType !== "partner") return false;
        return true;
      });
      setResults(filtered);
    } catch (err) {
      console.error("[AddPartnerSourceModal] Search failed", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setInlineError(null);
    setSaving(true);
    try {
      await makeApiRequest(
        "post",
        apiRoutes.addCompanySource(company.companyId),
        auth.user,
        effectiveCompanyId,
        { partnerId: selected.companyId, role }
      );
      toast({
        status: "success",
        title: "Partner added",
        description: `${selected.name} is now a ${role} source for ${company.name}.`,
      });
      onAdded();
    } catch (err) {
      console.error("[AddPartnerSourceModal] Add failed", err);
      const message = err.response?.data?.error || "Unknown error";
      const statusCode = err.response?.status;
      if (statusCode === 409 || statusCode === 400) {
        // Surface inline — caller chose a bad partner or picked a dup
        setInlineError(message);
      } else {
        toast({
          status: "destructive",
          title: "Failed to add partner",
          description: message,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Add Partner Source</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Attribute <span className="text-foreground font-medium">{company.name}</span> to a partner.
            </p>
          </div>
          <button
            className="btn btn-ghost h-8 w-8 p-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            className="input input-bordered w-full pl-9"
            placeholder="Search partners by name, slug, or company ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="max-h-64 overflow-y-auto -mx-2 px-2 mb-4">
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {query
                ? "No matching partners found."
                : "Start typing to search partners."}
            </div>
          ) : (
            <ul className="space-y-1">
              {results.map((c) => {
                const isSelected = selected?.companyId === c.companyId;
                return (
                  <li key={c.companyId}>
                    <button
                      type="button"
                      onClick={() => setSelected(c)}
                      className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg border transition-colors ${
                        isSelected
                          ? "border-brand bg-brand/10"
                          : "border-transparent hover:border-border hover:bg-muted/60"
                      }`}
                    >
                      <div
                        className={`p-1.5 rounded-md ${
                          isSelected ? "bg-brand/20" : "bg-muted"
                        }`}
                      >
                        <Building2
                          className={`h-4 w-4 ${
                            isSelected ? "text-brand" : "text-muted-foreground"
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {c.name}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.slug ? `${c.slug} · ` : ""}
                          {c.companyId}
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-brand shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Role selector */}
        <div className="mb-4">
          <label className="text-sm font-medium text-foreground block mb-2">
            Role
          </label>
          <div className="grid grid-cols-1 gap-1.5">
            {ROLE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  role === opt.value
                    ? "border-brand bg-brand/10"
                    : "border-border hover:bg-muted/60"
                }`}
              >
                <input
                  type="radio"
                  name="source-role"
                  value={opt.value}
                  checked={role === opt.value}
                  onChange={() => setRole(opt.value)}
                  className="mt-0.5 h-4 w-4 border-border text-brand focus:ring-brand"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {opt.label}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {opt.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {inlineError && (
          <div className="mb-3 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
            {inlineError}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-border">
          <button
            className="btn btn-ghost flex-1"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary flex-1"
            onClick={handleConfirm}
            disabled={!selected || saving}
          >
            {saving
              ? "Adding..."
              : selected
              ? `Add ${selected.name}`
              : "Select a partner"}
          </button>
        </div>
      </div>
    </div>
  );
}
